import React, { useState, useEffect } from 'react';

const ImagesTab = ({ API, adminToken, isDarkMode, showNotification }) => {
  const [images, setImages] = useState([]);
  const [imageCategories, setImageCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showAddImage, setShowAddImage] = useState(false);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Form states
  const [newImage, setNewImage] = useState({
    category: '',
    image_url: '',
    caption: '',
    is_active: true
  });

  const [newCategory, setNewCategory] = useState({
    name: '',
    emoji: '📷'
  });

  useEffect(() => {
    fetchData();
  }, [selectedCategory]);

  const fetchData = async () => {
    setIsLoading(true);
    await Promise.all([fetchImages(), fetchCategories()]);
    setIsLoading(false);
  };

  const fetchImages = async () => {
    try {
      const url = selectedCategory 
        ? `${API}/admin/buddy/images?category=${selectedCategory}` 
        : `${API}/admin/buddy/images`;
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setImages(data || []);
      }
    } catch (err) {
      console.error('Failed to fetch images:', err);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API}/admin/buddy/image-categories`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setImageCategories(data || []);
      }
    } catch (err) {
      console.error('Failed to fetch image categories:', err);
    }
  };

  const createImage = async () => {
    if (!newImage.image_url || !newImage.category) {
      showNotification('Image URL and category are required', 'error');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('category', newImage.category);
      formData.append('image_url', newImage.image_url);
      formData.append('caption', newImage.caption);
      formData.append('is_active', newImage.is_active);

      const res = await fetch(`${API}/admin/buddy/images`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` },
        body: formData
      });
      if (res.ok) {
        showNotification('Image added!', 'success');
        setShowAddImage(false);
        setNewImage({ category: '', image_url: '', caption: '', is_active: true });
        fetchImages();
      }
    } catch (err) {
      showNotification('Failed to add image', 'error');
    }
  };

  const updateImage = async () => {
    if (!editingImage) return;
    try {
      const formData = new FormData();
      formData.append('category', editingImage.category);
      formData.append('image_url', editingImage.image_url);
      formData.append('caption', editingImage.caption);
      formData.append('is_active', editingImage.is_active);

      const res = await fetch(`${API}/admin/buddy/images/${editingImage.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${adminToken}` },
        body: formData
      });
      if (res.ok) {
        showNotification('Image updated!', 'success');
        setEditingImage(null);
        fetchImages();
      }
    } catch (err) {
      showNotification('Failed to update image', 'error');
    }
  };

  const deleteImage = async (imageId) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      await fetch(`${API}/admin/buddy/images/${imageId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      showNotification('Image deleted', 'success');
      fetchImages();
    } catch (err) {
      showNotification('Failed to delete image', 'error');
    }
  };

  const createCategory = async () => {
    if (!newCategory.name) {
      showNotification('Category name is required', 'error');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('name', newCategory.name);
      formData.append('emoji', newCategory.emoji);

      const res = await fetch(`${API}/admin/buddy/image-categories`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` },
        body: formData
      });
      if (res.ok) {
        showNotification('Category created!', 'success');
        setShowAddCategory(false);
        setNewCategory({ name: '', emoji: '📷' });
        fetchCategories();
      }
    } catch (err) {
      showNotification('Failed to create category', 'error');
    }
  };

  const deleteCategory = async (categoryId) => {
    if (!window.confirm('Delete this category? Images in this category will become uncategorized.')) return;
    try {
      await fetch(`${API}/admin/buddy/image-categories/${categoryId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      showNotification('Category deleted', 'success');
      setSelectedCategory(null);
      fetchCategories();
    } catch (err) {
      showNotification('Failed to delete category', 'error');
    }
  };

  const updateCategory = async () => {
    if (!editingCategory || !editingCategory.name) {
      showNotification('Category name is required', 'error');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('name', editingCategory.name);
      formData.append('emoji', editingCategory.emoji || '📷');

      const res = await fetch(`${API}/admin/buddy/image-categories/${editingCategory.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${adminToken}` },
        body: formData
      });
      if (res.ok) {
        showNotification('Category updated!', 'success');
        setEditingCategory(null);
        fetchCategories();
      } else {
        showNotification('Failed to update category', 'error');
      }
    } catch (err) {
      showNotification('Failed to update category', 'error');
    }
  };

  const getDropboxDisplayUrl = (url) => {
    if (!url) return '';
    return url.replace('?dl=0', '?raw=1').replace('www.dropbox.com', 'dl.dropboxusercontent.com');
  };

  if (isLoading) {
    return <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div></div>;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          📸 Image Library ({images.length})
        </h3>
        <div className="flex gap-2">
          <button
            onClick={() => setShowAddCategory(true)}
            className="px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white text-sm rounded-lg"
          >
            + Category
          </button>
          <button
            onClick={() => setShowAddImage(true)}
            className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded-lg"
          >
            + Add Image
          </button>
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setSelectedCategory(null)}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
            selectedCategory === null
              ? 'bg-purple-600 text-white'
              : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          All ({images.length})
        </button>
        {imageCategories.map(cat => {
          const count = images.filter(img => img.category === cat.id).length;
          return (
            <div key={cat.id} className="flex items-center gap-1">
              <button
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1 ${
                  selectedCategory === cat.id
                    ? 'bg-purple-600 text-white'
                    : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {cat.emoji} {cat.name} ({count})
              </button>
              <button
                onClick={() => setEditingCategory({ ...cat })}
                className={`p-1.5 rounded text-xs ${isDarkMode ? 'text-blue-400 hover:bg-gray-700' : 'text-blue-600 hover:bg-gray-200'}`}
                title="Edit category"
              >
                ✏️
              </button>
              <button
                onClick={() => deleteCategory(cat.id)}
                className={`p-1.5 rounded text-xs ${isDarkMode ? 'text-red-400 hover:bg-gray-700' : 'text-red-600 hover:bg-gray-200'}`}
                title="Delete category"
              >
                ×
              </button>
            </div>
          );
        })}
      </div>

      {/* Images Grid */}
      {images.length === 0 ? (
        <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          <p className="text-4xl mb-2">📷</p>
          <p>No images yet. Add your first image!</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map(image => {
            const category = imageCategories.find(c => c.id === image.category);
            return (
              <div
                key={image.id}
                className={`rounded-lg overflow-hidden border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} ${!image.is_active ? 'opacity-50' : ''}`}
              >
                <div className="aspect-square relative">
                  <img
                    src={getDropboxDisplayUrl(image.image_url)}
                    alt={image.caption}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><text y="50" x="50" text-anchor="middle" font-size="40">🖼️</text></svg>'; }}
                  />
                  {!image.is_active && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <span className="text-white text-sm">Inactive</span>
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <p className={`text-sm font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    {image.caption || 'No caption'}
                  </p>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {category ? `${category.emoji} ${category.name}` : 'Uncategorized'}
                  </p>
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => setEditingImage(image)}
                      className="flex-1 px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deleteImage(image.id)}
                      className="flex-1 px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Image Modal */}
      {showAddImage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-md rounded-xl p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Add New Image</h3>
            
            <div className="space-y-4">
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Category *</label>
                <select
                  value={newImage.category}
                  onChange={(e) => setNewImage({ ...newImage, category: e.target.value })}
                  className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                >
                  <option value="">Select category...</option>
                  {imageCategories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.emoji} {cat.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Image URL (Dropbox) *</label>
                <input
                  type="text"
                  value={newImage.image_url}
                  onChange={(e) => setNewImage({ ...newImage, image_url: e.target.value })}
                  placeholder="https://www.dropbox.com/..."
                  className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                />
              </div>

              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Caption</label>
                <input
                  type="text"
                  value={newImage.caption}
                  onChange={(e) => setNewImage({ ...newImage, caption: e.target.value })}
                  placeholder="Traditional Moroccan Kaftan"
                  className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                />
              </div>

              {newImage.image_url && (
                <div className="mt-2">
                  <p className={`text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Preview:</p>
                  <img
                    src={getDropboxDisplayUrl(newImage.image_url)}
                    alt="Preview"
                    className="w-full h-32 object-cover rounded-lg"
                    onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><text y="50" x="50" text-anchor="middle" font-size="20">Invalid URL</text></svg>'; }}
                  />
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAddImage(false)}
                className={`flex-1 px-4 py-2 rounded-lg ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
              >
                Cancel
              </button>
              <button
                onClick={createImage}
                className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg"
              >
                Add Image
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Image Modal */}
      {editingImage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-md rounded-xl p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Edit Image</h3>
            
            <div className="space-y-4">
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Category</label>
                <select
                  value={editingImage.category}
                  onChange={(e) => setEditingImage({ ...editingImage, category: e.target.value })}
                  className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                >
                  <option value="">Select category...</option>
                  {imageCategories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.emoji} {cat.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Image URL</label>
                <input
                  type="text"
                  value={editingImage.image_url}
                  onChange={(e) => setEditingImage({ ...editingImage, image_url: e.target.value })}
                  className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                />
              </div>

              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Caption</label>
                <input
                  type="text"
                  value={editingImage.caption}
                  onChange={(e) => setEditingImage({ ...editingImage, caption: e.target.value })}
                  className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={editingImage.is_active}
                  onChange={(e) => setEditingImage({ ...editingImage, is_active: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="is_active" className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Active</label>
              </div>

              {editingImage.image_url && (
                <div className="mt-2">
                  <p className={`text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Preview:</p>
                  <img
                    src={getDropboxDisplayUrl(editingImage.image_url)}
                    alt="Preview"
                    className="w-full h-32 object-cover rounded-lg"
                  />
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setEditingImage(null)}
                className={`flex-1 px-4 py-2 rounded-lg ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
              >
                Cancel
              </button>
              <button
                onClick={updateImage}
                className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Category Modal */}
      {showAddCategory && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-sm rounded-xl p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Add Image Category</h3>
            
            <div className="space-y-4">
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Emoji</label>
                <input
                  type="text"
                  value={newCategory.emoji}
                  onChange={(e) => setNewCategory({ ...newCategory, emoji: e.target.value })}
                  className={`w-20 px-3 py-2 rounded-lg border text-center text-xl ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                />
              </div>

              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Category Name *</label>
                <input
                  type="text"
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                  placeholder="e.g., Clothing, Food, Places"
                  className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAddCategory(false)}
                className={`flex-1 px-4 py-2 rounded-lg ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
              >
                Cancel
              </button>
              <button
                onClick={createCategory}
                className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Category Modal */}
      {editingCategory && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-sm rounded-xl p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Edit Category</h3>
            
            <div className="space-y-4">
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Emoji</label>
                <input
                  type="text"
                  value={editingCategory.emoji}
                  onChange={(e) => setEditingCategory({ ...editingCategory, emoji: e.target.value })}
                  className={`w-20 px-3 py-2 rounded-lg border text-center text-xl ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                />
              </div>

              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Category Name *</label>
                <input
                  type="text"
                  value={editingCategory.name}
                  onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                  placeholder="e.g., Clothing, Food, Places"
                  className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setEditingCategory(null)}
                className={`flex-1 px-4 py-2 rounded-lg ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
              >
                Cancel
              </button>
              <button
                onClick={updateCategory}
                className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImagesTab;
